import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { ROUTES } from '#/config/routes';
import useDetailTable from '#/hooks/table/useDetailTable';
import { Button } from '#/modules/commonComponents/button';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import CustomerOrderWorkOrderPreviewDialog
  from '#/modules/crmMgmt/customerOrder/components/CustomerOrderWorkOrderPreviewDialog';
import { displayOrDash } from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';
import {
  mapCustomerOrderWorkOrderRows,
} from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderWorkOrders';
import WorkOrderStatusBadge from '#/modules/momMgmt/workOrder/components/WorkOrderStatusBadge';

function PreviewWorkOrderCell({ workOrder, customerOrderId }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        預覽
      </Button>
      <CustomerOrderWorkOrderPreviewDialog
        open={open}
        onOpenChange={setOpen}
        workOrder={workOrder}
        customerOrderId={customerOrderId}
      />
    </>
  );
}

PreviewWorkOrderCell.propTypes = {
  workOrder: PropTypes.object.isRequired,
  customerOrderId: PropTypes.string,
};

function buildWorkOrderColumns(customerOrderId) {
  return [
    {
      id: 'preview',
      header: '預覽',
      accessorFn: (row) => row.workOrderId ?? '',
      size: 88,
      enableSorting: false,
      cell: ({ row }) => (
        <PreviewWorkOrderCell
          workOrder={row.original}
          customerOrderId={customerOrderId}
        />
      ),
    },
    {
      id: 'workOrderNo',
      header: '工單號',
      accessorFn: (row) => displayOrDash(row.workOrderNo),
      size: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const id = row.original.workOrderId;
        const workOrderNo = displayOrDash(row.original.workOrderNo);
        return (
          <TruncatedTextWithTooltip
            text={workOrderNo}
            detailPath={id ? ROUTES.workOrderDetail(id) : null}
            tooltipContent={id ? '雙擊開啟單據（新分頁）' : null}
          />
        );
      },
      meta: { cellClassName: 'td_left' },
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      enableSorting: false,
      cell: ({ row }) => {
        const status = row.original.status;
        if (status) {
          return <WorkOrderStatusBadge status={status} size="sm" />;
        }
        return <span className="text-muted-foreground">—</span>;
      },
      meta: {
        statusPresentation: 'badge',
      },
    },
    {
      id: 'mtrNo',
      header: '料號',
      accessorFn: (row) => displayOrDash(row.mtrNo),
      size: 140,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
    },
    {
      id: 'mtrName',
      header: '物料名稱',
      accessorFn: (row) => displayOrDash(row.mtrName),
      size: 200,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
    },
    {
      id: 'qty',
      header: '數量',
      accessorFn: (row) => (
        row.qty == null || row.qty === '' ? '—' : String(row.qty)
      ),
      size: 96,
      enableSorting: false,
      meta: { cellClassName: 'td_right' },
    },
  ];
}

/**
 * 客訂詳情「工單」Tab：扁平 workOrderMtrs（含 inline status；列表不另打工單 detail）。
 * 預覽單次 GET detail 做狀態流轉；雙擊工單號進詳情。
 */
const CustomerOrderWorkOrdersSection = ({
  workOrderMtrs = [],
  customerOrderId = null,
}) => {
  const columns = useMemo(
    () => buildWorkOrderColumns(customerOrderId),
    [customerOrderId],
  );
  const rows = useMemo(
    () => mapCustomerOrderWorkOrderRows(workOrderMtrs),
    [workOrderMtrs],
  );

  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row, index) => String(row.id ?? `wo-${index}`),
  });

  if (rows.length === 0) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無關聯工單</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

CustomerOrderWorkOrdersSection.propTypes = {
  workOrderMtrs: PropTypes.arrayOf(PropTypes.object),
  customerOrderId: PropTypes.string,
};

export default CustomerOrderWorkOrdersSection;
